// Match CSV column headers to case-template field names.
// No optional chaining — gui Babel does not support it.

import { logFeature } from "./featureActivity"

export const SKIP_IMPORT = "__dont_import__"

export function normalizeHeader(value) {
  if (value == null) return ""
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
}

export function collectAtomicFields(schema) {
  const fields = [
    { name: "title", type: "string", display_name: "Title" },
    { name: "current_state", type: "string", display_name: "Current State" },
  ]
  if (!schema || !schema.fieldsets) return fields

  schema.fieldsets.forEach(function (fieldset) {
    if (!fieldset || !fieldset.fields) return
    fieldset.fields.forEach(function (field) {
      if (!field || !field.name) return
      if (field.name === "title" || field.name === "current_state") return
      // Include entity fields as a whole; skip nested entity columns for atomic.
      fields.push(field)
    })
  })
  return fields
}

export function collectEntityFields(entityField) {
  if (!entityField || !entityField.fields) return []
  return entityField.fields.filter(function (field) {
    return field && field.name
  })
}

export function collectEntityTables(schema) {
  const tables = []
  if (!schema || !schema.fieldsets) return tables
  schema.fieldsets.forEach(function (fieldset) {
    if (!fieldset || !fieldset.fields) return
    fieldset.fields.forEach(function (field) {
      if (field && field.type === "entity") {
        tables.push(field)
      }
    })
  })
  return tables
}

function findFieldMatch(csvHeader, templateFields, claimed) {
  const exact = String(csvHeader == null ? "" : csvHeader)
  const normalized = normalizeHeader(exact)

  function tryClaim(field) {
    if (!field || !field.name) return null
    if (claimed[field.name]) return null
    claimed[field.name] = true
    return field
  }

  let i
  for (i = 0; i < templateFields.length; i++) {
    if (templateFields[i].name === exact) {
      return tryClaim(templateFields[i])
    }
  }
  for (i = 0; i < templateFields.length; i++) {
    if (normalizeHeader(templateFields[i].name) === normalized) {
      return tryClaim(templateFields[i])
    }
  }
  for (i = 0; i < templateFields.length; i++) {
    const display = templateFields[i].display_name
    if (display != null && String(display) === exact) {
      return tryClaim(templateFields[i])
    }
  }
  for (i = 0; i < templateFields.length; i++) {
    const display = templateFields[i].display_name
    if (display != null && normalizeHeader(display) === normalized) {
      return tryClaim(templateFields[i])
    }
  }
  return null
}

/**
 * Auto-match CSV headers to template fields.
 * First match wins; duplicate targets leave later columns unmapped.
 * @returns {Array<{csvHeader: string, sample: *, fieldName: string|null, field: object|null}>}
 */
export function matchCsvHeaders(csvHeaders, templateFields, sampleRow) {
  const claimed = {}
  const headers = csvHeaders || []
  const sample = sampleRow || {}
  const mapped = headers.map(function (csvHeader) {
    const field = findFieldMatch(csvHeader, templateFields || [], claimed)
    return {
      csvHeader: csvHeader,
      sample: sample[csvHeader],
      fieldName: field ? field.name : null,
      field: field || null,
    }
  })
  const matched = mapped.filter(function (row) {
    return row.fieldName
  }).length
  logFeature("csv-mapping", "match", "Auto-matched CSV headers", {
    headers: headers.length,
    matched: matched,
  })
  return mapped
}

export function mappedFieldNames(mappings) {
  const names = []
  ;(mappings || []).forEach(function (row) {
    if (row && row.fieldName) {
      names.push(row.fieldName)
    }
  })
  return names
}

export function mappedFields(mappings, templateFields) {
  const byName = {}
  ;(templateFields || []).forEach(function (field) {
    if (field && field.name) byName[field.name] = field
  })
  const fields = []
  ;(mappings || []).forEach(function (row) {
    if (row && row.fieldName && byName[row.fieldName]) {
      fields.push(byName[row.fieldName])
    } else if (row && row.field) {
      fields.push(row.field)
    }
  })
  return fields
}

/**
 * Suggest default primary keys from mapped fields.
 * Prefer uniqueIdentifierField when mapped, else title.
 */
export function suggestDefaultPrimaries(mappedNames, uniqueIdentifierField) {
  const names = mappedNames || []
  if (uniqueIdentifierField && names.indexOf(uniqueIdentifierField) >= 0) {
    return [uniqueIdentifierField]
  }
  if (names.indexOf("title") >= 0) {
    return ["title"]
  }
  return []
}

/**
 * Pick the entity table whose column names overlap the CSV headers most.
 */
export function pickBestEntityTable(entityTables, csvHeaders) {
  const tables = entityTables || []
  if (tables.length === 0) return null
  if (tables.length === 1) return tables[0]

  const headerSet = {}
  ;(csvHeaders || []).forEach(function (header) {
    headerSet[normalizeHeader(header)] = true
    headerSet[String(header)] = true
  })

  let best = tables[0]
  let bestScore = -1
  tables.forEach(function (table) {
    let score = 0
    const fields = collectEntityFields(table)
    fields.forEach(function (field) {
      if (
        headerSet[field.name] ||
        headerSet[normalizeHeader(field.name)] ||
        (field.display_name &&
          (headerSet[String(field.display_name)] ||
            headerSet[normalizeHeader(field.display_name)]))
      ) {
        score += 1
      }
    })
    if (score > bestScore) {
      bestScore = score
      best = table
    }
  })
  return best
}

/**
 * Apply user overrides while preventing two columns mapping to the same field.
 * When assigning fieldName to index, clear any other mapping that used it.
 */
export function applyMappingOverride(mappings, index, fieldName, templateFields) {
  const next = (mappings || []).map(function (row) {
    return {
      csvHeader: row.csvHeader,
      sample: row.sample,
      fieldName: row.fieldName,
      field: row.field,
    }
  })
  if (index < 0 || index >= next.length) return next

  const target = fieldName || null
  if (target) {
    next.forEach(function (row, i) {
      if (i !== index && row.fieldName === target) {
        row.fieldName = null
        row.field = null
      }
    })
  }

  let field = null
  if (target && templateFields) {
    for (let i = 0; i < templateFields.length; i++) {
      if (templateFields[i].name === target) {
        field = templateFields[i]
        break
      }
    }
  }
  next[index].fieldName = target
  next[index].field = field
  return next
}

/**
 * Rewrite Papa-parsed rows so only mapped columns remain, renamed to field names.
 */
export function rewriteCsvRows(rows, mappings) {
  const active = (mappings || []).filter(function (row) {
    return row && row.fieldName
  })
  return (rows || []).map(function (row) {
    const out = {}
    active.forEach(function (mapping) {
      out[mapping.fieldName] = row[mapping.csvHeader]
    })
    return out
  })
}

export function fieldSelectOptions(templateFields, mappings, currentIndex) {
  const claimed = {}
  ;(mappings || []).forEach(function (row, i) {
    if (row && row.fieldName && i !== currentIndex) {
      claimed[row.fieldName] = true
    }
  })
  const options = [{ name: SKIP_IMPORT, label: "Don't import" }]
  ;(templateFields || []).forEach(function (field) {
    if (!field || !field.name) return
    const current =
      mappings && mappings[currentIndex] && mappings[currentIndex].fieldName === field.name
    if (claimed[field.name] && !current) return
    const label = field.display_name
      ? field.name + " (" + field.display_name + ")"
      : field.name
    options.push({ name: field.name, label: label, type: field.type })
  })
  return options
}
